<script setup lang="ts">
import { CheckCircleIcon, XCircleIcon } from '@gohighlevel/ghl-icons/24/outline'
import { CustomModal } from '../CustomModal'
import { computed, ref, watch } from 'vue'
import { ModalEvents } from '../../../types/components/CProductList.schema'

const props = defineProps({
  isAllValid: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
  productListErrors: { type: Array, default: () => [] },
  validations: { type: Object, default: () => ({}) },
  notificationSettings: { type: Object, default: () => ({}) },
  settingsErrors: { type: Array, default: () => [] },
  redirectUrlErrors: { type: Array, default: () => [] },
  showOnlyProductListErrors: { type: Boolean, default: false },
  customValueLinkageErrors: { type: Array, default: () => [] },
})
defineEmits([ModalEvents.ON_SUCCESS, ModalEvents.ON_CANCEL])
const showErrorModal = ref(false)
const showLoading = ref(false)
watch(props, updatedProps => {
  showErrorModal.value = updatedProps.show
  showLoading.value = updatedProps.loading
})

const scheduleErrors = computed(() => props.productListErrors)
const settingsErrors = computed(() => props.settingsErrors)
const showBasicErrors = computed(() => props.showOnlyProductListErrors)
</script>

<template>
  <CustomModal
    :close-on-success="false"
    :loading="showLoading"
    :disabled="showLoading"
    :show="showErrorModal"
    :title="
      isAllValid ? $t('common.validTemplate') : $t('common.invalidTemplate')
    "
    :description="!isAllValid ? $t('validations.invalid.tempMustFields') : ''"
    @on-cancel="$emit(ModalEvents.ON_CANCEL)"
    @on-success="$emit(ModalEvents.ON_SUCCESS)"
    :cancelActionText="isAllValid ? $t('common.cancel') : $t('common.ok')"
    :successActionText="isAllValid ? $t('common.send') : ''"
    :modalType="isAllValid ? 'success' : 'error'"
  >
    <div class="flex flex-col" v-if="!isAllValid">
      <div class="flex items-center" v-if="!showBasicErrors">
        <span
          :class="`h-4 w-4 mr-2 ${
            !notificationSettings || validations.isNotificationSettingsValid
              ? 'text-success-500'
              : 'text-red-500'
          }`"
          ><CheckCircleIcon
            v-if="
              !notificationSettings || validations.isNotificationSettingsValid
            "
          />
          <XCircleIcon
            v-if="
              notificationSettings && !validations.isNotificationSettingsValid
            "
          />
        </span>
        {{
          !notificationSettings || validations.isNotificationSettingsValid
            ? $t('common.validEmailConfiguration')
            : $t('common.invalidEmailConfiguration')
        }}
      </div>
      <div class="flex items-center" v-if="!showBasicErrors">
        <span>
          <ul style="list-style-type: disc">
            <li v-for="(settingsError, index) in settingsErrors" :key="index">
              {{ settingsError }}
            </li>
          </ul>
        </span>
      </div>

      <div class="flex items-center">
        <span
          :class="`h-4 w-4 mr-2 ${
            validations.isProductListValid ? 'text-success-500' : 'text-red-500'
          }`"
          ><CheckCircleIcon v-if="validations.isProductListValid" />
          <XCircleIcon v-if="!validations.isProductListValid" />
        </span>
        {{
          validations.isProductListValid
            ? $t('validations.invalid.validProductListConfig')
            : $t('validations.invalid.productListConfig')
        }}
      </div>
      <div class="flex items-center">
        <span>
          <ul style="list-style-type: disc">
            <li v-for="(schduleError, index) in scheduleErrors" :key="index">
              {{ schduleError }}
            </li>
          </ul>
        </span>
      </div>

      <div class="flex items-center" v-if="!showBasicErrors">
        <span
          :class="`h-4 w-4 mr-2 ${
            validations.isRedirectUrlSettingsValid
              ? 'text-success-500'
              : 'text-red-500'
          }`"
          ><CheckCircleIcon v-if="validations.isRedirectUrlSettingsValid" />
          <XCircleIcon v-if="!validations.isRedirectUrlSettingsValid" />
        </span>
        {{
          validations.isRedirectUrlSettingsValid
            ? $t('validations.invalid.validRedirectUrlConfig')
            : $t('validations.invalid.invalidRedirectUrlConfig')
        }}
      </div>
      <div class="flex items-center" v-if="!showBasicErrors">
        <span>
          <ul style="list-style-type: disc">
            <li
              v-for="(redirectUrlError, index) in redirectUrlErrors"
              :key="index"
            >
              {{ redirectUrlError }}
            </li>
          </ul>
        </span>
      </div>

      <div class="flex items-center">
        <span
          :class="`h-4 w-4 mr-2 ${
            validations.isCustomValueLinkageValid
              ? 'text-success-500'
              : 'text-red-500'
          }`"
          ><CheckCircleIcon v-if="validations.isCustomValueLinkageValid" />
          <XCircleIcon v-if="!validations.isCustomValueLinkageValid" />
        </span>
        {{
          validations.isCustomValueLinkageValid
            ? $t('validations.invalid.validCustomValueLinkageConfig')
            : $t('validations.invalid.invalidCustomValueLinkageConfig')
        }}
      </div>
      <div class="flex items-center">
        <span>
          <ul style="list-style-type: disc">
            <li
              v-for="(
                customValueLinkageError, index
              ) in customValueLinkageErrors"
              :key="index"
            >
              {{ customValueLinkageError }}
            </li>
          </ul>
        </span>
      </div>

      <div class="flex items-center">
        <span
          :class="`h-4 w-4 mr-2 ${
            validations.isImageSizeValid ? 'text-success-500' : 'text-red-500'
          }`"
        >
          <CheckCircleIcon v-if="validations.isImageSizeValid" />
          <XCircleIcon v-if="!validations.isImageSizeValid" />
        </span>
        {{
          validations.isImageSizeValid
            ? $t('validations.invalid.validImageSizeConfig')
            : $t('validations.invalid.invalidImageSizeConfig')
        }}
      </div>

      <div class="flex items-start mt-3">
        <span
          :class="`h-4 w-4 mr-2 mt-1 ${
            validations.isDocumentNameValid
              ? 'text-success-500'
              : 'text-red-500'
          }`"
        >
          <CheckCircleIcon v-if="validations.isDocumentNameValid" />
          <XCircleIcon v-if="!validations.isDocumentNameValid" />
        </span>
        {{
          validations.isDocumentNameValid
            ? $t('validations.invalid.documentTitleValid')
            : $t('validations.invalid.documentTitleInvalid')
        }}
      </div>
    </div>
  </CustomModal>
</template>

<style lang="scss" scoped>
.text-success-500 {
  --tw-text-opacity: 1;
  color: rgb(18 183 106 / var(--tw-text-opacity));
}
</style>
